import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { borisMantineTheme } from './theme/mantineTheme';
import { auth } from './config/supabase';
import { isRecoveryCallback } from './utils/authRedirect';
import { resolveUserRole, isSuperadmin } from './utils/userRole';
import type { UserRole } from './types/user';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import AppLayout from './components/layout/AppLayout';
import AdminGuard from './components/admin/AdminGuard';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminStoresList from './components/admin/AdminStoresList';
import AdminStoreNew from './components/admin/AdminStoreNew';
import AdminStoreDetail from './components/admin/AdminStoreDetail';
import AdminUsersList from './components/admin/AdminUsersList';
import AdminUserNew from './components/admin/AdminUserNew';
import { AdminLoading } from './components/admin/adminShared';
import './App.css';

function homePathForRole(role: UserRole | null): string {
  return isSuperadmin(role) ? '/admin' : '/dashboard';
}

function AppRoutes() {
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(isRecoveryCallback());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkUser = async () => {
      const { user: currentUser } = await auth.getCurrentUser();
      setUser(currentUser);
      setLoading(false);
    };

    checkUser();

    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
        navigate('/reset-password', { replace: true });
      }

      if (event === 'SIGNED_IN' && session?.user) {
        resolveUserRole(session.user).then((role) => {
          if (location.pathname === '/' || location.pathname === '/login') {
            navigate(homePathForRole(role), { replace: true });
          }
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (isRecoveryCallback()) {
      setPasswordRecovery(true);
    }
  }, [location]);

  useEffect(() => {
    if (!user) {
      setUserRole(null);
      setRoleLoading(false);
      return;
    }

    setRoleLoading(true);
    resolveUserRole(user)
      .then(setUserRole)
      .finally(() => setRoleLoading(false));
  }, [user]);

  const handleLoginSuccess = () => {
    // User will be updated via auth state change
  };

  const mustResetPassword = passwordRecovery || isRecoveryCallback();
  const defaultHome = homePathForRole(userRole);

  if (loading || (user && roleLoading)) {
    return (
      <div className="App">
        <AdminLoading />
      </div>
    );
  }

  const dashboardRoute = (
    <Route
      path="/dashboard"
      element={
        mustResetPassword ? (
          <Navigate to="/reset-password" replace />
        ) : user ? (
          <AppLayout>
            <Dashboard />
          </AppLayout>
        ) : (
          <Navigate to="/" replace />
        )
      }
    />
  );

  const metricsSectionRoutes = (
    <>
      <Route
        path="/ventas"
        element={
          mustResetPassword ? (
            <Navigate to="/reset-password" replace />
          ) : user ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/cyd"
        element={
          mustResetPassword ? (
            <Navigate to="/reset-password" replace />
          ) : user ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/cyd/*"
        element={
          mustResetPassword ? (
            <Navigate to="/reset-password" replace />
          ) : user ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/operaciones"
        element={
          mustResetPassword ? (
            <Navigate to="/reset-password" replace />
          ) : user ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/operaciones/*"
        element={
          mustResetPassword ? (
            <Navigate to="/reset-password" replace />
          ) : user ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/clientes"
        element={
          mustResetPassword ? (
            <Navigate to="/reset-password" replace />
          ) : user ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/clientes/*"
        element={
          mustResetPassword ? (
            <Navigate to="/reset-password" replace />
          ) : user ? (
            <AppLayout>
              <Dashboard />
            </AppLayout>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
    </>
  );

  return (
    <div className="App">
      <Routes>
        <Route
          path="/reset-password"
          element={
            user || mustResetPassword ? (
              <ResetPassword onComplete={() => setPasswordRecovery(false)} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/"
          element={
            mustResetPassword ? (
              <Navigate to="/reset-password" replace />
            ) : user ? (
              <Navigate to={defaultHome} replace />
            ) : (
              <Login onLoginSuccess={handleLoginSuccess} />
            )
          }
        />
        <Route
          path="/login"
          element={
            mustResetPassword ? (
              <Navigate to="/reset-password" replace />
            ) : user ? (
              <Navigate to={defaultHome} replace />
            ) : (
              <Login onLoginSuccess={handleLoginSuccess} />
            )
          }
        />

        <Route
          path="/admin"
          element={
            mustResetPassword ? (
              <Navigate to="/reset-password" replace />
            ) : (
              <AdminGuard user={user}>
                <AdminLayout />
              </AdminGuard>
            )
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="stores" element={<AdminStoresList />} />
          <Route path="stores/new" element={<AdminStoreNew />} />
          <Route path="stores/:storeId" element={<AdminStoreDetail />} />
          <Route path="users" element={<AdminUsersList />} />
          <Route path="users/new" element={<AdminUserNew />} />
        </Route>

        {dashboardRoute}
        {metricsSectionRoutes}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <MantineProvider theme={borisMantineTheme}>
      <Router>
        <AppRoutes />
      </Router>
    </MantineProvider>
  );
}

export default App;
